<script setup>
import moment from 'moment'
import { cookieRef } from '@/@layouts/stores/config'
import { HaiType, haiTypeTitle } from '@/enums/quality/hai-type'
import { HaiCaseStatus, haiCaseStatusTitle } from '@/enums/quality/hai-case-status'
import HaiTable from '@/views/quality/infection-control/hai/HaiTable.vue'

definePage({
  meta: {
    subject: 'quality.infection_control.menu',
    action: 'hai_surveillance',
  },
})

const router = useRouter()
const unitId = cookieRef('selectedUnit')

// Filter options
const haiTypeOptions = Object.values(HaiType).map(val => ({
  title: haiTypeTitle(val),
  value: val,
}))

const haiStatusOptions = Object.values(HaiCaseStatus).map(val => ({
  title: haiCaseStatusTitle(val),
  value: val,
}))

// Location dropdown (searchable, is_ic_enabled)
const searchLocation = ref('')
const debouncedSearchLocation = refDebounced(searchLocation, 500)

const { data: locationData, isFetching: fetchingLocations, execute: fetchLocations } = useApi(createUrl('/masters/locations', {
  query: {
    limit: 20,
    q: debouncedSearchLocation,
    search: {
      unit_id: unitId,
      is_ic_enabled: 1,
    },
  },
}), {
  beforeFetch: ({ cancel }) => {
    if (!unitId.value) cancel()
  },
  immediate: false,
})

watch(() => unitId.value, () => {
  if (unitId.value) fetchLocations()
}, { immediate: true })

watch(debouncedSearchLocation, () => {
  fetchLocations()
})

// Filter state
const searchQuery = ref('')
const selectedHaiType = ref(null)
const selectedStatus = ref(null)
const selectedLocation = ref(null)

// Date range (single field)
const defaultFromDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultToDate = moment().format('YYYY-MM-DD')
const dateRange = ref(`${defaultFromDate} to ${defaultToDate}`)
const fromDate = ref(defaultFromDate)
const toDate = ref(defaultToDate)

const dateRangeConfig = ref({
  mode: 'range',
  dateFormat: 'Y-m-d',
  enableTime: false,
  defaultDate: [defaultFromDate, defaultToDate],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
})

// Computed filters passed to HaiTable
const computedFilters = computed(() => {
  const filters = {}

  if (selectedHaiType.value) filters.hai_type = selectedHaiType.value
  if (selectedStatus.value) filters.status = selectedStatus.value
  if (selectedLocation.value) filters.location_id = selectedLocation.value
  if (fromDate.value) filters.from_date = fromDate.value
  if (toDate.value) filters.to_date = toDate.value

  return filters
})

const refHaiTable = ref(null)

// Export
const isExporting = ref(false)

const exportHaiCases = async () => {
  isExporting.value = true

  const url = createUrl('infection-control/hai-cases/export', {
    query: {
      ...computedFilters.value,
      q: searchQuery.value || undefined,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {
    console.error('Export error:', error)
  })

  isExporting.value = false
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <!-- Filters -->
      <VCardText class="d-flex flex-column gap-4">
        <!-- Row 1: Filters -->
        <VRow dense>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <AppSelect
              v-model="selectedHaiType"
              :items="haiTypeOptions"
              item-title="title"
              item-value="value"
              placeholder="HAI Type"
              clearable
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <AppSelect
              v-model="selectedStatus"
              :items="haiStatusOptions"
              item-title="title"
              item-value="value"
              placeholder="Status"
              clearable
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <AppAutocomplete
              v-model="selectedLocation"
              v-model:search="searchLocation"
              :items="locationData ?? []"
              item-title="name"
              item-value="id"
              placeholder="Location"
              :loading="fetchingLocations"
              clearable
              :return-object="false"
            />
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <AppDateTimePicker
              v-model="dateRange"
              placeholder="Date Range"
              :config="dateRangeConfig"
            />
          </VCol>
        </VRow>

        <!-- Row 2: Search + Add button -->
        <div class="d-flex flex-wrap align-center gap-4">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search by code, patient, organism..."
            density="compact"
            clearable
            style="inline-size: 300px;"
          />

          <VSpacer />

          <VBtn
            variant="outlined"
            prepend-icon="tabler-download"
            :loading="isExporting"
            @click="exportHaiCases"
          >
            Export
          </VBtn>

          <VBtn
            variant="flat"
            color="primary"
            prepend-icon="tabler-plus"
            @click="router.push({ name: 'app-quality-infection-control-hai-entry' })"
          >
            Add HAI Case
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- Table -->
      <HaiTable
        ref="refHaiTable"
        :filters="computedFilters"
        :search-query="searchQuery"
      />
    </VCard>
  </section>
</template>
